import { AgenturModel } from 'app/models/agentur.model';
import { makeObservable, observable } from 'mobx';
import { formatNum } from 'app/utils';
import { AgtBestandHistoryVM } from 'app/models/berichte/agt.bestand.history.vm';
import colors from 'assets/scss/colors.module.scss';
import { AgtBestandPosModel } from 'app/models/berichte/agt.bestand.pos.model';
import { useEffect, useRef } from 'react';
import { init, EChartsOption, ECharts } from 'echarts';
import { debounce, get } from 'lodash';

interface IBtStueckTimeModel {
	title: string;
	agentur?: AgenturModel;
	data?: AgtBestandHistoryVM[];
	fill: string;
	btKey: string;
	btLabel: string;
	stKey: string;
	stLabel: string;
	timeKey: string;
	timeLabel: string;
}
export class BtStueckTimeModel {
	constructor(opts: IBtStueckTimeModel) {
		makeObservable(this);
		this.title = opts.title;
		if (opts.agentur) {
			this.agentur = opts.agentur;
		}
		if (opts.data) {
			this.data = opts.data;
		}
		this.fill = opts.fill;
		this.btKey = opts.btKey;
		this.btLabel = opts.btLabel;
		this.stKey = opts.stKey;
		this.stLabel = opts.stLabel;
		this.timeKey = opts.timeKey;
		this.timeLabel = opts.timeLabel;
	}
	title: string;
	agentur?: AgenturModel;
	@observable
	data: AgtBestandHistoryVM[] | AgtBestandPosModel[] = [];
	fill: string = 'blue';
	btKey: string;
	btLabel: string;
	stKey: string;
	stLabel: string;
	timeKey: string;
	timeLabel: string;
}

export const BtStueckTimeModelChart = (parms: BtStueckTimeModel) => {
	const chartRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// Extract time values and data values from the data array
		// Use lodash get() to handle nested property paths like 'production.val.bt'
		const xAxisData = parms.data.map((item: any) => get(item, parms.timeKey));
		const btData = parms.data.map((item: any) => get(item, parms.btKey));
		const stData = parms.data.map((item: any) => get(item, parms.stKey));

		const option: EChartsOption = {
			tooltip: {
				trigger: 'axis',
				axisPointer: {
					type: 'cross',
					crossStyle: {
						color: '#999',
					},
				},
			},
			legend: {
				data: [parms.btLabel, parms.stLabel],
				bottom: 0,
			},
			grid: {
				left: 0,
				right: 0,
				top: 10,
				bottom: 30,
				containLabel: true,
			},
			xAxis: {
				type: 'category',
				data: xAxisData,
				axisPointer: {
					type: 'shadow',
				},
			},
			yAxis: [
				{
					type: 'value',
					name: parms.btLabel,
					position: 'left',
					axisLabel: {
						formatter: (value: number) => formatNum(value),
					},
					nameRotate: 90,
					nameLocation: 'middle',
					nameGap: 60,
				},
				{
					type: 'value',
					name: parms.stLabel,
					position: 'right',
					axisLabel: {
						formatter: (value: number) => formatNum(value),
					},
					nameRotate: 90,
					nameLocation: 'middle',
					nameGap: 60,
				},
			],
			series: [
				{
					name: parms.btLabel,
					type: 'bar',
					yAxisIndex: 0,
					data: btData,
					itemStyle: {
						color: colors.primary,
					},
					barMaxWidth: 40,
				},
				{
					name: parms.stLabel,
					type: 'line',
					yAxisIndex: 1,
					data: stData,
					connectNulls: true,
					smooth: true,
					lineStyle: {
						color: colors.black,
					},
					itemStyle: {
						color: colors.black,
					},
				},
			],
		};

		let chart: ECharts | null = null;
		if (chartRef.current !== null) {
			chart = init(chartRef.current, null, { renderer: 'svg' });
			chart.setOption(option);
		}

		const handleResize = debounce(() => {
			chart?.resize();
		}, 300);
		window.addEventListener('resize', handleResize);

		return () => {
			chart?.dispose();
			window.removeEventListener('resize', handleResize);
		};
	}, [parms.data, parms.btKey, parms.stKey, parms.timeKey, parms.btLabel, parms.stLabel]);

	return <div style={{ width: '100%', height: 300, background: '#ffffff' }} ref={chartRef}></div>;
};
